import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { PaginatedList } from '../../../app/core/data/paginated-list.model';
import { RemoteData } from '../../../app/core/data/remote-data';
import { BrowseDefinition } from '../../../app/core/shared/browse-definition.model';
import { getFirstCompletedRemoteData } from '../../../app/core/shared/operators';
import { MenuResolverService as BaseMenuResolverService } from '../../../app/menu-resolver.service';
import { MenuID } from '../../../app/shared/menu/menu-id.model';
import { ExternalLinkMenuItemModel } from '../../../app/shared/menu/menu-item/models/external-link.model';
import { LinkMenuItemModel } from '../../../app/shared/menu/menu-item/models/link.model';
import { TextMenuItemModel } from '../../../app/shared/menu/menu-item/models/text.model';
import { MenuItemType } from '../../../app/shared/menu/menu-item-type.model';

/**
 * BfS-specific menu resolver. Replaces the public menu with the following order:
 * "Über DORIS" (with sub-entries) - "Das gesamte Repositorium" - "Bereiche & Sammlungen" - "Alle Publikationen".
 * The admin menu is inherited unchanged from the base resolver.
 */
@Injectable()
export class MenuResolverService extends BaseMenuResolverService {

  /**
   * Initialize all menu sections and items for {@link MenuID.PUBLIC}
   */
  createPublicMenu$(): Observable<boolean> {
    const menuList: any[] = [
      /* About DORIS */
      {
        id: 'about_doris',
        active: false,
        visible: true,
        index: 0,
        model: {
          type: MenuItemType.TEXT,
          text: 'menu.section.about_doris',
        } as TextMenuItemModel,
      },
      {
        id: 'faq',
        parentID: 'about_doris',
        active: false,
        visible: true,
        index: 0,
        model: {
          type: MenuItemType.LINK,
          text: 'menu.section.faq',
          link: '/info/faq',
        } as LinkMenuItemModel,
      },
      {
        id: 'contact',
        parentID: 'about_doris',
        active: false,
        visible: true,
        index: 1,
        model: {
          type: MenuItemType.LINK,
          text: 'menu.section.feedback',
          link: '/info/feedback',
        } as LinkMenuItemModel,
      },
      {
        id: 'accessibility',
        parentID: 'about_doris',
        active: false,
        visible: true,
        index: 2,
        model: {
          type: MenuItemType.LINK,
          text: 'menu.section.accessibility',
          link: '/info/barrierefreiheit',
        } as LinkMenuItemModel,
      },
      {
        id: 'privacy',
        parentID: 'about_doris',
        active: false,
        visible: true,
        index: 3,
        model: {
          type: MenuItemType.EXTERNAL,
          text: 'menu.section.privacy',
          href: 'https://www.bfs.de/datenschutz',
        } as ExternalLinkMenuItemModel,
      },
      {
        id: 'social_media',
        parentID: 'about_doris',
        active: false,
        visible: true,
        index: 4,
        model: {
          type: MenuItemType.EXTERNAL,
          text: 'menu.section.social_media',
          href: 'https://www.bfs.de/social-media',
        } as ExternalLinkMenuItemModel,
      },
      /* Communities & Collections tree */
      {
        id: 'browse_global_communities_and_collections',
        active: false,
        visible: true,
        index: 2,
        model: {
          type: MenuItemType.LINK,
          text: 'menu.section.browse_global_communities_and_collections',
          link: '/community-list',
        } as LinkMenuItemModel,
      },
      /* All publications (search over all items) */
      {
        id: 'all_publications',
        active: false,
        visible: true,
        index: 3,
        model: {
          type: MenuItemType.LINK,
          disabled: false,
          text: 'menu.section.all_publications',
          link: '/search',
          // discovery configuration "allPublications" (BfS discovery.xml): only items, no Person entities
          queryParams: { configuration: 'allPublications' },
        } as LinkMenuItemModel,
      },
    ];
    menuList.forEach((menuSection) => this.addPublicSection(menuSection));

    // Read the different Browse-By types from config and add them to the browse menu
    this.browseService.getBrowseDefinitions()
      .pipe(getFirstCompletedRemoteData<PaginatedList<BrowseDefinition>>())
      .subscribe((browseDefListRD: RemoteData<PaginatedList<BrowseDefinition>>) => {
        if (browseDefListRD.hasSucceeded) {
          browseDefListRD.payload.page.forEach((browseDef: BrowseDefinition) => {
            this.addPublicSection({
              id: `browse_global_by_${browseDef.id}`,
              parentID: 'browse_global',
              active: false,
              visible: true,
              model: {
                type: MenuItemType.LINK,
                text: `menu.section.browse_global_by_${browseDef.id}`,
                link: `/browse/${browseDef.id}`,
              } as LinkMenuItemModel,
            });
          });
          /* Browse */
          this.addPublicSection({
            id: 'browse_global',
            active: false,
            visible: true,
            index: 1,
            model: {
              type: MenuItemType.TEXT,
              text: 'menu.section.browse_global',
            } as TextMenuItemModel,
          });
        }
      });

    return this.waitForMenu$(MenuID.PUBLIC);
  }

  /**
   * Add a section to the public menu that persists on route changes
   */
  protected addPublicSection(menuSection: any): void {
    this.menuService.addSection(MenuID.PUBLIC, Object.assign(menuSection, {
      shouldPersistOnRouteChange: true,
    }));
  }
}
